import React, { useState } from 'react';
import { DollarSign, Package, Truck, Scale, Clock, MapPin, CheckCircle2, Phone, Mail, ArrowRight, TrendingUp, Calculator } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import QuoteRequestForm from '../components/QuoteRequestForm';

const ShippingCostCanadaToIndiaPage = () => {
  const { toast } = useToast();

  const costFactors = [
    {
      icon: Scale,
      title: "Parcel Weight",
      description: "Heavier parcels cost more to ship. Rates are typically calculated per kilogram, with bulk discounts available for shipments over 50kg."
    },
    {
      icon: Package,
      title: "Dimensions",
      description: "Dimensional weight pricing applies. Large, lightweight packages may be charged based on size rather than actual weight (Length × Width × Height ÷ 5000)."
    },
    {
      icon: Clock,
      title: "Shipping Speed",
      description: "Express services (3-7 days) are faster than economy options. Choose the service level that balances your budget and timeline."
    },
    {
      icon: Truck,
      title: "Courier Carrier",
      description: "Different carriers offer varying rates. DHL, FedEx, UPS, and Purolator each have competitive pricing for different weight ranges and destinations."
    },
    {
      icon: MapPin,
      title: "Destination City",
      description: "Shipping to major Indian cities like Mumbai or Delhi may cost less than remote areas due to better infrastructure and frequent service routes."
    },
    {
      icon: TrendingUp,
      title: "Customs Requirements",
      description: "Proper documentation reduces delays and potential fees. Accurate declarations and complete paperwork help avoid additional customs charges."
    }
  ];

  const priceRanges = [
    {
      weight: "1 kg",
      economy: "$24 - $43",
      express: "$48 - $81",
      days: "10-15 / 3-7 days"
    },
    {
      weight: "5 kg",
      economy: "$76 - $143",
      express: "$171 - $266",
      days: "10-15 / 3-7 days"
    },
    {
      weight: "10 kg",
      economy: "$133 - $238",
      express: "$304 - $456",
      days: "10-15 / 3-7 days"
    },
    {
      weight: "20 kg",
      economy: "$238 - $428",
      express: "$551 - $808",
      days: "10-15 / 3-7 days"
    },
    {
      weight: "50 kg",
      economy: "$523 - $903",
      express: "$1,140 - $1,710",
      days: "10-15 / 3-7 days"
    },
    {
      weight: "100 kg+",
      economy: "From $7.99/kg",
      express: "$10 - $15/kg",
      days: "10-15 / 3-7 days"
    }
  ];

  const carriers = [
    {
      name: "DHL",
      speed: "Fast",
      cost: "Mid to High",
      delivery: "3-7 days",
      description: "Excellent for time-sensitive shipments with premium tracking and reliability. Known for extensive India network and consistent delivery times."
    },
    {
      name: "FedEx",
      speed: "Fast",
      cost: "Mid to High",
      delivery: "3-7 days",
      description: "Strong international presence with competitive express rates. Offers comprehensive door-to-door service with customs clearance support."
    },
    {
      name: "UPS",
      speed: "Fast",
      cost: "Mid to High",
      delivery: "4-8 days",
      description: "Reliable global shipping with various service tiers. Good balance of cost and speed with solid tracking capabilities and customer service."
    },
    {
      name: "Economy Options",
      speed: "Standard",
      cost: "Low to Mid",
      delivery: "10-15 days",
      description: "Most affordable option for non-urgent shipments. Consolidation services and slower transit times help reduce costs significantly."
    }
  ];

  const cities = [
    { name: 'Toronto', link: '/international-courier-toronto' },
    { name: 'Brampton', link: '/international-shipping-brampton' },
    { name: 'Mississauga', link: '/international-shipping-mississauga' },
    { name: 'Surrey', link: '/international-shipping-surrey' },
    { name: 'Vancouver', link: '/international-shipping-vancouver' },
    { name: 'Nova Scotia', link: '/international-shipping-nova-scotia' }
  ];

  const steps = [
    {
      number: 1,
      title: "Get Instant Quote",
      description: "Provide your parcel details (weight, dimensions, origin, destination). We compare rates from DHL, FedEx, UPS instantly."
    },
    {
      number: 2,
      title: "Compare Carrier Rates",
      description: "Review side-by-side pricing from multiple carriers. Choose between economy and express options based on your budget."
    },
    {
      number: 3,
      title: "Select Best Price",
      description: "Pick the carrier and service that best fits your needs. No hidden fees – see all costs upfront before booking."
    },
    {
      number: 4,
      title: "Book & Pay",
      description: "Complete your booking online. We prepare all shipping labels and customs documentation for you."
    },
    {
      number: 5,
      title: "Ship with Confidence",
      description: "Drop off at partner locations or schedule pickup. Track your shipment in real-time from Canada to India."
    }
  ];

  const faqs = [
    {
      question: "How much does it cost to ship to India from Canada?",
      answer: "Shipping costs from Canada to India vary based on weight, dimensions, and speed. Documents ship from $44 (standard, 7-9 days) or $77 (express, 2-5 days). Parcels have fixed pricing: 1kg is $45, 5kg is $85, 10kg is $149, 15kg is $195. For 23kg+, doorstep delivery is $9.99/kg. For India airport-to-airport bulk shipments (100kg minimum), rates start at $7.99/kg. Contact Shippzy for an accurate quote."
    },
    {
      question: "What courier is cheapest to India from Canada?",
      answer: "The cheapest courier varies depending on your shipment's weight, dimensions, and destination. Economy services through consolidation are generally the most affordable option. For smaller parcels, rates between DHL, FedEx, and UPS can differ by 20-40%. Shippzy compares all major carriers automatically to find you the lowest rate for your specific shipment, ensuring you always get the best price available."
    },
    {
      question: "How long does shipping from Canada to India take?",
      answer: "Delivery times depend on the service selected. Standard parcel delivery typically takes 3-7 business days. Documents ship in 7-9 days (standard) or 2-5 days (express). Delivery times include customs clearance processing, which usually takes 1-3 days in India."
    },
    {
      question: "Are there additional costs beyond the shipping rate?",
      answer: "The shipping rate we quote includes pickup (if applicable), transportation, and delivery. However, customs duties and taxes imposed by Indian authorities are separate charges paid by the recipient or sender based on the shipment's declared value. These vary by item type and value. Shippzy helps you prepare proper documentation to minimize delays and unexpected charges."
    },
    {
      question: "How is dimensional weight calculated for shipping costs?",
      answer: "Dimensional weight is calculated as: (Length × Width × Height in cm) ÷ 5000. If the dimensional weight is higher than the actual weight, you'll be charged based on dimensional weight. For example, a large lightweight box (50×50×50 cm = 25kg dimensional weight) weighing only 5kg would be charged at 25kg. We help optimize packaging to reduce dimensional weight charges."
    },
    {
      question: "Can I get a discount on shipping costs to India?",
      answer: "Yes! Shippzy offers several ways to save: (1) Bulk discounts for shipments over 50kg, with even better rates at 100kg+, (2) Economy service can save 40-60% compared to express, (3) Using partner drop-off locations instead of pickup saves collection fees, (4) Proper packaging to minimize dimensional weight, and (5) Off-peak shipping when rates are lower. Contact us to discuss volume discounts for regular shippers."
    },
    {
      question: "What affects the final shipping cost the most?",
      answer: "The two biggest factors are weight/dimensions and shipping speed. A 10kg parcel via economy service costs about $133-$238, while the same parcel via express costs $304-$456 – nearly double. Proper packaging to reduce dimensional weight can also significantly impact cost. Choosing the right carrier for your specific weight range can save 20-30%. We help optimize all these factors."
    },
    {
      question: "Do shipping costs include insurance?",
      answer: "Basic liability coverage (typically up to $100) is usually included in the shipping rate. Additional insurance is available for higher-value items at an extra cost, typically 1-3% of the declared value. We recommend insurance for electronics, jewelry, or any shipment valued over $200. Insurance claims are processed through the carrier."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Shipping Cost Information - Canada to India",
    "provider": {
      "@type": "Organization",
      "name": "Shippzy Inc.",
      "telephone": "+1-437-616-0758",
      "email": "ship@shippzy.ca"
    },
    "areaServed": ["CA", "IN"],
    "serviceType": "International Shipping Cost Comparison",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "24",
      "highPrice": "81",
      "priceCurrency": "CAD",
      "offerCount": "4"
    }
  };

  return (
    <>
      <Helmet>
        <title>Shipping Cost from Canada to India | International Courier Rates – Shippzy</title>
        <meta name="description" content="Find out the shipping cost from Canada to India. Compare courier rates from DHL, FedEx and UPS and get the best international shipping price with Shippzy." />
        <meta name="keywords" content="shipping cost Canada to India, courier rates Canada India, international shipping prices, how much to ship to India, DHL rates India, FedEx cost India, UPS shipping cost" />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-cost-canada-to-india" />
        
        <meta property="og:title" content="Shipping Cost from Canada to India | Courier Rates – Shippzy" />
        <meta property="og:description" content="Compare shipping costs from Canada to India. Get the best courier rates from DHL, FedEx, UPS with Shippzy." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.shippzy.ca/shipping-cost-canada-to-india" />
        
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                💰 Compare Rates & Save
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Shippzy Shipping Rates
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Affordable international shipping from Canada. Transparent pricing for documents, parcels, luggage and relocation shipments to India, UK, Dubai/UAE, and Nepal.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Price Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Factors Affecting Shipping Cost</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Several key factors determine the final cost of shipping from Canada to India
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {costFactors.map((factor, index) => {
                const IconComponent = factor.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{factor.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{factor.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Shippzy Shipping Rates</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Affordable international shipping from Canada. Transparent pricing for documents, parcels, luggage and relocation shipments.
              </p>
            </div>

            {/* Document Shipping */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Document Shipping</h3>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <Card className="border-2 border-blue-500 shadow-xl">
                  <CardContent className="pt-8 text-center">
                    <p className="text-lg text-gray-700 mb-2">Standard Document Courier</p>
                    <p className="text-5xl font-bold text-blue-600 mb-2">$44</p>
                    <p className="text-gray-600 mb-4">CAD FLAT</p>
                    <p className="text-sm text-gray-600">Estimated delivery: 7–9 business days</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-red-500 shadow-xl">
                  <CardContent className="pt-8 text-center">
                    <p className="text-lg text-gray-700 mb-2">Express Document Courier</p>
                    <p className="text-5xl font-bold text-red-600 mb-2">$77</p>
                    <p className="text-gray-600 mb-4">CAD FLAT</p>
                    <p className="text-sm text-gray-600">Estimated delivery: 2–5 business days</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Parcel Shipping */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Parcel Shipping</h3>
              <div className="max-w-4xl mx-auto">
                <Card className="shadow-xl">
                  <CardContent className="pt-8">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2">
                            <th className="text-left py-3 px-4 text-gray-900 font-semibold">Weight</th>
                            <th className="text-right py-3 px-4 text-gray-900 font-semibold">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b"><td className="py-3 px-4 text-gray-700">First 1 kg</td><td className="text-right py-3 px-4 font-semibold text-gray-900">$45 CAD</td></tr>
                          <tr className="border-b"><td className="py-3 px-4 text-gray-700">Additional weight</td><td className="text-right py-3 px-4 font-semibold text-gray-900">$15 CAD/kg</td></tr>
                          <tr className="border-b"><td className="py-3 px-4 text-gray-700">5 kg</td><td className="text-right py-3 px-4 font-semibold text-gray-900">$85 CAD</td></tr>
                          <tr className="border-b"><td className="py-3 px-4 text-gray-700">10 kg</td><td className="text-right py-3 px-4 font-semibold text-gray-900">$149 CAD</td></tr>
                          <tr className="border-b"><td className="py-3 px-4 text-gray-700">15 kg</td><td className="text-right py-3 px-4 font-semibold text-gray-900">$195 CAD</td></tr>
                          <tr><td className="py-3 px-4 text-gray-700 font-semibold">23 kg and above</td><td className="text-right py-3 px-4 font-bold text-red-600 text-lg">$9.99 CAD/kg</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-6 text-center">
                      <p className="text-lg font-semibold text-gray-900">Delivery Estimate: 3–7 Business Days</p>
                      <p className="text-sm text-gray-600 mt-2">Fixed weight prices take priority. Chargeable weight may depend on actual or volumetric weight.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Bulk & Relocation */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Bulk & Relocation Shipping</h3>
              <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                <Card className="border-2 border-yellow-500 shadow-xl">
                  <CardContent className="pt-8 text-center">
                    <p className="text-lg text-gray-700 mb-2">Airport-to-Airport</p>
                    <p className="text-5xl font-bold text-yellow-600 mb-2">$7.99</p>
                    <p className="text-gray-600 mb-4">CAD per kg</p>
                    <div className="bg-gray-100 p-3 rounded-lg mb-4">
                      <p className="text-sm font-semibold text-gray-900">Min: 100 kg</p>
                      <p className="text-sm text-gray-700">INDIA ONLY</p>
                    </div>
                    <p className="text-xs text-gray-600">Airport collection at destination</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-green-500 shadow-xl">
                  <CardContent className="pt-8 text-center">
                    <p className="text-lg text-gray-700 mb-2">Bulk Doorstep</p>
                    <p className="text-5xl font-bold text-green-600 mb-2">$8.49</p>
                    <p className="text-gray-600 mb-4">CAD per kg</p>
                    <div className="bg-gray-100 p-3 rounded-lg mb-4">
                      <p className="text-sm font-semibold text-gray-900">Min: 138 kg</p>
                      <p className="text-sm text-gray-700">IN, UK, UAE, NP</p>
                    </div>
                    <p className="text-xs text-gray-600">Doorstep delivery</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-purple-500 shadow-xl">
                  <CardContent className="pt-8 text-center">
                    <p className="text-lg text-gray-700 mb-2">Doorstep Shipping</p>
                    <p className="text-5xl font-bold text-purple-600 mb-2">$9.99</p>
                    <p className="text-gray-600 mb-4">CAD per kg</p>
                    <div className="bg-gray-100 p-3 rounded-lg mb-4">
                      <p className="text-sm font-semibold text-gray-900">Min: 23 kg</p>
                      <p className="text-sm text-gray-700">IN, UK, UAE, NP</p>
                    </div>
                    <p className="text-xs text-gray-600">Doorstep delivery</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Free Packaging */}
            <div className="mb-16">
              <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 shadow-xl">
                <CardContent className="pt-8 text-center">
                  <Package className="w-16 h-16 mx-auto mb-4 text-yellow-600" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Free Boxes & Packaging Supplies</h3>
                  <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
                    Book your shipment in-store at Shippzy and receive standard shipping boxes and packaging supplies at no additional cost.
                  </p>
                  <p className="text-sm text-gray-600 mb-6">This offer applies to in-store bookings.</p>
                  <Button className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3">
                    Visit Our Brampton Office
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Important Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <h4 className="font-semibold text-gray-900 mb-3">Important Information</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• All prices are in Canadian dollars.</li>
                  <li>• Chargeable weight may depend on actual or volumetric weight.</li>
                  <li>• Delivery times are estimates, not guarantees.</li>
                  <li>• Customs duties and taxes, if assessed, may be additional.</li>
                  <li>• Final pricing depends on shipment eligibility, dimensions and service availability.</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Carrier Comparison Section - Keep Existing */}
        <section className="py-20 bg-gradient-to-br from-red-50 to-yellow-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Courier Comparison: DHL, FedEx & UPS</h2>
              <p className="text-xl text-gray-600">
                Understanding the differences helps you choose the right carrier for your needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {carriers.map((carrier, index) => (
                <Card key={index} className="hover:shadow-xl transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Truck className="w-6 h-6 text-red-600" />
                      {carrier.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Speed</p>
                        <p className="font-semibold text-gray-900">{carrier.speed}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Cost Level</p>
                        <p className="font-semibold text-gray-900">{carrier.cost}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600 mb-1">Delivery</p>
                        <p className="font-semibold text-gray-900">{carrier.delivery}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{carrier.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="mt-8 bg-gradient-to-r from-gray-50 to-gray-100">
              <CardContent className="pt-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Why Compare with Shippzy?</h3>
                <p className="text-lg text-gray-700 leading-relaxed text-center max-w-3xl mx-auto">
                  Instead of checking each carrier individually, Shippzy compares rates from all major couriers instantly. This saves you time and ensures you never overpay. We have negotiated volume discounts with carriers and pass the savings directly to you, often resulting in rates 20-40% lower than booking directly with carriers.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Cities We Serve in Canada</h2>
              <p className="text-xl text-gray-600">
                Get competitive shipping rates from all major Canadian cities to India
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cities.map((city, index) => (
                <Card key={index} className={`text-center ${city.link ? 'hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer border-2 hover:border-red-500' : 'hover:shadow-md transition-shadow'}`}>
                  <CardContent className="pt-8 pb-8">
                    <MapPin className="w-10 h-10 text-red-600 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{city.name}</h3>
                    {city.link ? (
                      <a 
                        href={city.link} 
                        className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold mt-2 group"
                      >
                        View Rates
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    ) : (
                      <p className="text-gray-600 text-sm mt-2">Competitive rates available</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping to India from Canada
                </a>
                <span className="text-gray-400">•</span>
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options from Canada to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-shipping-brampton" className="text-red-600 hover:text-red-700 font-semibold underline">
                  International shipping from Brampton
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  International courier services in Toronto
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">How Shippzy's Pricing Works</h2>
              <p className="text-xl text-gray-600">5 simple steps to get the best shipping cost</p>
            </div>

            <div className="space-y-6">
              {steps.map((step, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex gap-6 items-start">
                      <div className="w-12 h-12 bg-gradient-to-r from-red-600 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-2xl font-bold text-white">{step.number}</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Common questions about shipping costs from Canada to India</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Get Your Shipping Cost Quote</h2>
              <p className="text-xl">Compare rates instantly and find the best price for your shipment</p>
            </div>
            <QuoteRequestForm />
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Transparent Shipping Costs from Canada to India</h3>
            <p className="text-gray-300 mb-6">
              Compare courier rates from DHL, FedEx, and UPS. Get instant quotes with no hidden fees. Save up to 75% on international shipping with Shippzy's competitive pricing and expert service.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3"
                onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Calculate Shipping Cost
              </Button>
              <Button 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3"
                onClick={() => window.location.href = '/'}
              >
                View All Services
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCostCanadaToIndiaPage;
